import React from "react"
import useStorageState from "@myvp/shared/src/hooks/use-storage-state"
import { dispatchStorageSyncMessage } from "@myvp/shared/src/functions/sync-session-storage"
import { isStringEnumValue } from "@myvp/shared/src/functions/type-guards"
import { SurveyLocation } from "@myvp/study-preview/src/gql/graphql"
import { type StudyPreviewQueryData } from "@myvp/study-preview/src/study-preview.model"

export interface SchedulePreviewFilterOptions {
  groups: Record<string, string | null>
  requiredGroups: Record<string, string | null>
  location: SurveyLocation | "all"
}

const storageKey = "myvp/study-preview/scheduleFilters"

export const useSchedulePreviewFilterOptions = (
  data: StudyPreviewQueryData,
  collectionId: string
) => {
  const [filterMap, setFilterMap] = useStorageState(
    (storedData: unknown): Record<string, SchedulePreviewFilterOptions> => {
      const locationSet = new Set(
        data.getStudyPreview.schedules.map((schedule) => schedule.location)
      )
      // if this fails for some reason, it's better to return the empty initial state instead of crashing.
      try {
        if (storedData && typeof storedData === "object") {
          return {
            ...storedData,
            [collectionId]: resolveSchedulePreviewFilterOptions(
              {
                groupTypes: data.getStudyPreview.groupTypes,
                locations: locationSet,
              },
              storedData[collectionId as keyof typeof storedData]
            ),
          }
        }
      } catch {}
      return {
        [collectionId]: resolveSchedulePreviewFilterOptions(
          {
            groupTypes: data.getStudyPreview.groupTypes,
            locations: locationSet,
          },
          undefined
        ),
      }
    },
    storageKey,
    {
      callInitialValueOnMount: true,
      onStorageWrite: dispatchStorageSyncMessage,
    }
  )
  const filters = React.useMemo(() => {
    return filterMap[collectionId]
  }, [filterMap, collectionId])
  const setFilters = React.useCallback(
    (update: React.SetStateAction<SchedulePreviewFilterOptions>) => {
      setFilterMap((prevFilterMap) => {
        return {
          ...prevFilterMap,
          [collectionId]:
            typeof update === "function"
              ? update(prevFilterMap[collectionId])
              : update,
        }
      })
    },
    [setFilterMap, collectionId]
  )
  return [filters, setFilters] as const
}

export const resolveSchedulePreviewFilterOptions = (
  data: {
    groupTypes: StudyPreviewQueryData["getStudyPreview"]["groupTypes"]
    locations: ReadonlySet<SurveyLocation>
  },
  storedData: unknown
): SchedulePreviewFilterOptions => {
  const initialData: SchedulePreviewFilterOptions = {
    groups: Object.fromEntries(
      data.groupTypes.map((groupType) => [groupType.id, null])
    ),
    requiredGroups: Object.fromEntries(
      data.groupTypes
        .filter((groupType) => groupType.triggeredByEvents.length === 0)
        .map((groupType) => [groupType.id, null])
    ),
    location: "all",
  }

  if (
    !storedData ||
    typeof storedData !== "object" ||
    // check location
    !("location" in storedData) ||
    (storedData.location !== "all" &&
      !isStringEnumValue(SurveyLocation)(storedData.location)) ||
    // check if x.groups is an object
    !("groups" in storedData) ||
    !storedData.groups ||
    typeof storedData.groups !== "object" ||
    // check if x.requiredGroups is an object
    !("requiredGroups" in storedData) ||
    !storedData.requiredGroups ||
    typeof storedData.requiredGroups !== "object"
  ) {
    return initialData
  }

  const groupTypeMap = new Map(
    data.groupTypes.map((groupType) => [groupType.id, groupType])
  )
  const hasInvalidGroup = Object.entries(storedData.groups).some(
    ([key, value]) => {
      // value is not a string or null => invalid
      if (value !== null && typeof value !== "string") {
        return true
      }
      const groupType = groupTypeMap.get(key)
      // if the key doesn't correspond to a group type id => invalid
      if (!groupType) {
        return true
      }
      // a vaild group should either be `null` or correspond to some group in the group type
      return (
        value !== null && groupType.groups.every((group) => group.id !== value)
      )
    }
  )
  if (hasInvalidGroup) {
    return initialData
  }

  return {
    // if there is a location filter set but we won't show the location filter, we should
    // reset the location filter to "all". This can happen when the collection is in Draft status.
    location: data.locations.size > 1 ? storedData.location : "all",
    requiredGroups: {
      // merge groups in case some groupTypes are missing from `storedData.requiredGroups`
      // this can happen when the collection is in Draft status and the user adds new group types.
      ...initialData.requiredGroups,
      ...storedData.requiredGroups,
    },
    groups: {
      // merge groups in case some groupTypes are missing from `storedData.groups`
      // this can happen when the collection is in Draft status and the user adds new group types.
      ...initialData.groups,
      ...storedData.groups,
    },
  }
}

const tryParseJson = (json: unknown): unknown => {
  if (typeof json !== "string") {
    return json
  }
  try {
    return JSON.parse(json)
  } catch {
    return null
  }
}

export const updateHandlersForSchedulePreviewFilterOptions = {
  [storageKey]: (previous: unknown, current: unknown): string => {
    return JSON.stringify(
      Object.assign(
        {},
        ...[previous, current].map((value) => {
          const parsed = tryParseJson(value)
          return parsed && typeof parsed === "object" ? parsed : {}
        })
      )
    )
  },
}
